import { Injectable, inject } from '@angular/core';

import { OktaAuth, type UserClaims } from '@okta/okta-auth-js';

import { defer, type Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import type { AuthConfig } from './auth.models';
import { BECKS_AUTH_CONFIG } from './auth.tokens';

/**
 * Scrub scopes to ensure `openid` is included
 *
 * @param scopes Okta scopes
 */
const scrubScopes = (scopes: string[]): string[] => {
  if (scopes.indexOf('openid') >= 0) {
    return scopes;
  }

  return ['openid', ...scopes];
};

@Injectable()
export class AuthService {
  private readonly _config = inject<AuthConfig>(BECKS_AUTH_CONFIG);

  isAuthenticated$: Observable<boolean>;

  protected _oktaAuth: OktaAuth;

  constructor() {
    /**
     * Scrub scopes to ensure 'openid' is included
     */
    this._config.scopes = Array.isArray(this._config.scopes) ? scrubScopes(this._config.scopes) : ['openid', 'email'];

    /**
     * Create Okta Auth client
     */
    this._oktaAuth = new OktaAuth(this._config);

    /**
     * Create auth observables
     */
    this.isAuthenticated$ = defer(() => this._oktaAuth.isAuthenticated()).pipe(
      map((isAuthenticated) => isAuthenticated),
      distinctUntilChanged()
    );
  }

  /**
   * Performs a full page redirect to Okta based on the initial configuration.
   *
   * @param fromUri Path to push the user to after successful authentication
   */
  async loginRedirect(fromUri?: string): Promise<void> {
    return this._oktaAuth.signInWithRedirect({
      originalUri: fromUri,
    });
  }

  /**
   * Sign out from current session with a redirect to Okta to
   * terminate session cookie
   */
  async logoutRedirect(redirectUri?: string): Promise<void> {
    // If a relative path was passed, convert to absolute URI
    if (redirectUri?.startsWith('/')) {
      redirectUri = window.location.origin + redirectUri;
    }

    // redirect to Okta to kill session
    await this._oktaAuth.signOut({
      postLogoutRedirectUri: redirectUri,
      clearTokensBeforeRedirect: true,
      revokeAccessToken: true,
      revokeRefreshToken: true,
    });
  }

  /**
   * Returns the current accessToken in the tokenManager.
   *
   * @returns access token or undefined, if one was not found
   */
  async getAccessToken(): Promise<string | null> {
    try {
      const accessToken = await this._oktaAuth.getOrRenewAccessToken();
      return accessToken;
    } catch {
      // The user no longer has an existing SSO session in the browser.
      // (OIDC error `login_required`)
      // Ask the user to authenticate again.
      return null;
    }
  }

  /**
   * Returns user claims from the /userinfo endpoint.
   * https://developer.okta.com/docs/reference/api/oidc/#userinfo
   *
   * @return User token claims (user information)
   */
  async getUserInfo(): Promise<UserClaims> {
    return this._oktaAuth.token.getUserInfo();
  }

  /**
   * Parses the tokens from the callback URL.
   */
  async handleRedirect(): Promise<void> {
    if (this._oktaAuth.isLoginRedirect()) {
      // handle storing tokens
      await this._oktaAuth.handleRedirect();
    }
  }
}
